import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { fmtDate } from "@/lib/format";

export const ARRAY_KEYS = ["category", "customer", "salesman", "account", "fs_group", "company"];

export const CATEGORY_GROUPS = [
  ["Revenue", [["erp_sales", "Sales"]]],
  ["Cost of goods sold", [["erp_cost_of_sales", "Cost of goods sold"]]],
  ["Expenses", [["cos_expenses", "Cost of sales expenses"], ["salaries", "Salaries"], ["utilities", "Utilities"], ["rent", "Rent"], ["maintenance_repairs", "Maintenance and repairs"],
    ["transportation_logistics", "Transportation and logistics"], ["marketing_sales", "Marketing and sales"], ["admin_office", "Admin and office"],
    ["erp_cogs_expenses", "Production expenses"], ["sales_discount", "Sales discount"], ["depreciation", "Depreciation"],
    ["loan_interest", "Finance cost"]]],
];
export const CATEGORY_OPTIONS = CATEGORY_GROUPS.flatMap(([g, items]) => items.map(([value, label]) => ({ value, label: `${label} · ${g}` })));
export const CATEGORY_LABEL = Object.fromEntries(CATEGORY_GROUPS.flatMap(([, items]) => items));

export const dayLabel = (iso) => fmtDate(iso);

/** Shared filter state, persisted in the URL (?from&to&category&customer&salesman&account&fs_group&company). */
export function useFilters(defaultRange) {
  const [sp, setSp] = useSearchParams();
  const f = useMemo(() => ({
    from: sp.get("from"), to: sp.get("to"),
    ...Object.fromEntries(ARRAY_KEYS.map((k) => [k, sp.getAll(k)])),
  }), [sp]);
  const dated = Boolean(f.from && f.to);
  const range = useMemo(() => (dated ? [f.from, f.to] : defaultRange), [dated, f.from, f.to, defaultRange]);
  const update = useCallback((patch) => setSp((cur) => {
    const n = new URLSearchParams(cur);
    Object.entries(patch).forEach(([k, v]) => {
      n.delete(k);
      if (Array.isArray(v)) v.forEach((x) => n.append(k, x));
      else if (v && v !== "all") n.set(k, v);
    });
    return n;
  }, { replace: true }), [setSp]);
  const clear = useCallback(() => setSp(new URLSearchParams(), { replace: true }), [setSp]);
  const query = useCallback((extra = {}) => {
    const p = new URLSearchParams();
    if (range) { p.set("from", range[0]); p.set("to", range[1]); }
    if (dated) p.set("dated", "true");
    ARRAY_KEYS.forEach((k) => f[k].forEach((v) => p.append(k, v)));
    Object.entries(extra).forEach(([k, v]) => v != null && p.set(k, v));
    return p.toString();
  }, [f, range, dated]);
  const active = dated || ARRAY_KEYS.some((k) => f[k].length);
  return { f, range, dated, update, clear, query, key: query(), active };
}
